import {
  clearDatadogUser,
  getActiveRum,
  setDatadogUser,
} from "@/lib/consent/datadog";

/**
 * Product analytics (Datadog RUM custom actions).
 *
 * Every function is a no-op unless the visitor has accepted analytics and
 * Datadog is running (see `lib/consent/datadog.ts`), and on the server.
 */

interface EventContext {
  [key: string]: unknown;
}

/** Track a custom action/event. */
export function trackEvent(eventName: string, context?: EventContext): void {
  const rum = getActiveRum();

  if (!rum) return;

  try {
    rum.addAction(eventName, context);
  } catch {
    // Analytics must never affect the page.
  }
}

/** Movie interactions. */
export const trackMovie = {
  viewed: (movieId: string, movieTitle: string) => {
    trackEvent("movie_viewed", {
      movie_id: movieId,
      movie_title: movieTitle,
    });
  },

  addedToWatchlist: (movieId: string, movieTitle: string) => {
    trackEvent("movie_added_to_watchlist", {
      movie_id: movieId,
      movie_title: movieTitle,
    });
  },

  removedFromWatchlist: (movieId: string, movieTitle: string) => {
    trackEvent("movie_removed_from_watchlist", {
      movie_id: movieId,
      movie_title: movieTitle,
    });
  },

  markedAsWatched: (movieId: string, movieTitle: string) => {
    trackEvent("movie_marked_watched", {
      movie_id: movieId,
      movie_title: movieTitle,
    });
  },

  unmarkedAsWatched: (movieId: string, movieTitle: string) => {
    trackEvent("movie_unmarked_watched", {
      movie_id: movieId,
      movie_title: movieTitle,
    });
  },

  rated: (movieId: string, movieTitle: string, rating?: number) => {
    trackEvent("movie_rated", {
      movie_id: movieId,
      movie_title: movieTitle,
      ...(rating === undefined ? {} : { rating }),
    });
  },
};

/** Authentication events. */
export const trackAuth = {
  signedIn: (provider: string) => {
    trackEvent("user_signed_in", { provider });
  },

  signedOut: () => {
    trackEvent("user_signed_out", {});
  },
};

/** Search and discovery. */
export const trackSearch = {
  performed: (query: string, resultsCount: number) => {
    trackEvent("search_performed", {
      query,
      results_count: resultsCount,
    });
  },

  filterApplied: (filterType: string, filterValue: string) => {
    trackEvent("filter_applied", {
      filter_type: filterType,
      filter_value: filterValue,
    });
  },

  sortChanged: (sortBy: string) => {
    trackEvent("sort_changed", {
      sort_by: sortBy,
    });
  },
};

/**
 * Attach the signed-in user's opaque id to analytics. The email parameter
 * is accepted for backwards compatibility but never sent anywhere.
 */
export function setUserContext(userId: string, _email?: string): void {
  if (typeof window === "undefined") return;

  try {
    setDatadogUser(userId);
  } catch {
    // Analytics must never affect the page.
  }
}

/** Forget the user on sign-out. */
export function clearUserContext(): void {
  if (typeof window === "undefined") return;

  try {
    clearDatadogUser();
  } catch {
    // Analytics must never affect the page.
  }
}
